import React from "react";
import { PropTypes } from "prop-types";
import { MrbButton } from "mrb/components/button";
import { MrbSelect } from "mrb/components/select";
import { MrbToggle } from "mrb/components/checkbox";
import { PremiumBadge, Image } from "common/components";
import { observer } from "mobx-react";
import { useRootStore } from "mrb/core/contexts";
import { map, isNil } from "lodash";

function NakiStoreHomeSectionTemplate({ nakiStoreHomeSectionViewStore, t }) {
    return <EditPanelContainer nakiStoreHomeSectionViewStore={nakiStoreHomeSectionViewStore} t={t} />;
}

const EditPanelContainer = observer(function EditPanelContainer({ nakiStoreHomeSectionViewStore, t }) {
    return <EditPanel nakiStoreHomeSectionViewStore={nakiStoreHomeSectionViewStore} t={t} />;
});

const EditPanel = observer(function EditPanel({ nakiStoreHomeSectionViewStore, t }) {
    const {
        item,
        isSystemSection,
        setIsActive,
        onTitleChange,
        contentSelectStore,
        onSelectItem,
        isAddContentButtonDisabled,
        deleteSection,
        selectedItemExists,
    } = nakiStoreHomeSectionViewStore;
    return (
        <div className="c-device-menu__item c-device-menu__item--secondary u-mar--bottom--tny">
            <div className="u-width--100">
                <div className="c-device__header">
                    <TitleInput item={item} onChange={onTitleChange} />
                    <MrbToggle
                        label="Visible in the App"
                        labelClassNameExtend=" u-mar--top--tny"
                        id={`section-${item.id}`}
                        value={item.isActive}
                        onChange={(event) => setIsActive(event.target.checked)}
                    />

                    <div className="u-mar--left--auto u-display--flex u-gap--sml">
                        {!isSystemSection && (
                            <MrbButton
                                label="Delete"
                                className="c-btn c-btn--base c-btn--ghost"
                                onClick={deleteSection}
                            />
                        )}
                    </div>
                </div>

                {!isSystemSection && (
                    <div>
                        <div className="u-separator--primary"></div>
                        <div className="c-device__header">
                            <MrbSelect
                                store={contentSelectStore}
                                disabled={!item.isActive}
                                formatOptionLabel={({ item }) => <OptionLabel item={item} />}
                                classNameExtend="c-dropdown--dark c-dropdown--base u-width--400--px"
                            />
                            <MrbButton
                                t={t}
                                label="Add"
                                classNameOverride="c-btn c-btn--med c-btn--primary"
                                onClick={onSelectItem}
                                disabled={isAddContentButtonDisabled}
                            />
                            {selectedItemExists && (
                                <div className="c-device-menu__note c-device-menu__note--secondary u-mar--left--sml">
                                    {contentSelectStore.selectedItem.title} already selected.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <ContentPanel nakiStoreHomeSectionViewStore={nakiStoreHomeSectionViewStore} t={t} />
            </div>
        </div>
    );
});

const ContentPanel = observer(function ContentPanel({ nakiStoreHomeSectionViewStore, t }) {
    const { item, deleteItem } = nakiStoreHomeSectionViewStore;

    if (item.items.length === 0) return null;

    return <div className="c-device__list">{map(item.items, (item) => renderItem(item, deleteItem))}</div>;
});

function renderItem(item, onDelete) {
    if (!isNil(item.podcast)) {
        return <Podcast podcast={item.podcast} onDelete={() => onDelete(item.id)} />;
    } else if (!isNil(item.nakiNigunArtist)) {
        return <NakiNigunArtist artist={item.nakiNigunArtist} onDelete={() => onDelete(item.id)} />;
    } else if (!isNil(item.nakiNigunAlbum)) {
        return <NakiNigunAlbum album={item.nakiNigunAlbum} onDelete={() => onDelete(item.id)} />;
    }

    return null;
}

function Podcast({ podcast, onDelete }) {
    const rootStore = useRootStore();
    let coverImageUrl;
    if (podcast.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createPodcastCoverImageUrl(podcast.coverImageId, 200, 200);
    }
    return (
        <div className="c-device__list__item">
            <Image src={coverImageUrl} />
            <PremiumBadge premiumSize="secondary" isPremium={podcast.isPremium} />
            <p className="c-device__item__title">{podcast.title}</p>
            <MrbButton
                className="c-btn c-btn--base c-btn--ghost c-device__btn"
                icon="u-icon u-icon--sml u-icon--close--primary"
                onClick={onDelete}
            />
        </div>
    );
}

function NakiNigunArtist({ artist, onDelete }) {
    const rootStore = useRootStore();
    let coverImageUrl;
    if (artist.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createNakiNigunArtistCoverImageUrl(artist.coverImageId, 200, 200);
    }
    return (
        <div className="c-device__list__item">
            <Image src={coverImageUrl} />
            <PremiumBadge premiumSize="secondary" isPremium={artist.isPremium} />
            <p className="c-device__item__title">{artist.name}</p>
            <MrbButton
                className="c-btn c-btn--base c-btn--ghost c-device__btn"
                icon="u-icon u-icon--sml u-icon--close--primary"
                onClick={onDelete}
            />
        </div>
    );
}

function NakiNigunAlbum({ album, onDelete }) {
    const rootStore = useRootStore();
    let coverImageUrl;
    if (album.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createNakiNigunAlbumCoverImageUrl(album.coverImageId, 200, 200);
    }
    return (
        <div className="c-device__list__item">
            <Image src={coverImageUrl} />
            <PremiumBadge premiumSize="secondary" isPremium={album.isPremium} />
            <p className="c-device__item__title">{album.title}</p>
            <MrbButton
                className="c-btn c-btn--base c-btn--ghost c-device__btn"
                icon="u-icon u-icon--sml u-icon--close--primary"
                onClick={onDelete}
            />
        </div>
    );
}

const TitleInput = observer(function TitleInput({ item, onChange }) {
    return (
        <div className="u-width--100 u-width--300--max">
            <label className="u-type--sml">Section Title</label>
            <input
                className="c-input c-input--text c-input--med c-input--outline u-mar--top--tny"
                value={item.title}
                disabled={!item.isActive}
                onChange={onChange}
            />
        </div>
    );
});

function OptionLabel({ item }) {
    let icon = null;
    if (!isNil(item.podcastId)) {
        icon = "podcasts";
    } else if (!isNil(item.nakiNigunArtistId)) {
        icon = "nigun";
    } else if (!isNil(item.nakiNigunAlbumId)) {
        icon = "nigun";
    }
    return (
        <span>
            <i className={`u-icon u-icon--med u-icon--${icon} u-mar--right--tny`} />
            {item.title}
            {item.isPremium && <i className="u-icon u-icon--med u-icon--premium u-mar--left--tny" />}
        </span>
    );
}

NakiStoreHomeSectionTemplate.propTypes = {
    nakiStoreHomeSectionViewStore: PropTypes.object.isRequired,
};

export default observer(NakiStoreHomeSectionTemplate);
